import debounce from 'lodash.debounce'

/**
 * Header menu — the floating pill expands to reveal the main navigation.
 *
 * Opens on hover where there's a real pointer, and on click/tap (or Enter/Space
 * on the toggle) everywhere. The panel animates between a height of 0 and the
 * measured height of its content: `height: auto` can't be transitioned, and the
 * panel has to stay `overflow: hidden` for the collapse to clip cleanly.
 *
 * Motion itself lives in css/base/header.css, which also disables it under
 * `prefers-reduced-motion` — setting the height here stays instant either way.
 */
export function initNavMenu() {
    const nav = document.querySelector('[data-nav]')
    if (!nav) return

    const toggle = nav.querySelector('[data-nav-toggle]')
    const panel = nav.querySelector('[data-nav-panel]')
    const content = panel?.firstElementChild
    if (!toggle || !panel || !content) return

    let isOpen = false
    let hoverOpenedAt = 0

    const setOpen = (open) => {
        if (open === isOpen) return
        isOpen = open

        nav.classList.toggle('is-open', open)
        toggle.setAttribute('aria-expanded', String(open))
        // Measured on the content, not the panel: once the panel carries a pixel
        // height its own scrollHeight reports that height, not the content's.
        panel.style.height = open ? `${content.offsetHeight}px` : '0px'
    }

    /*
     * Hover opens from the toggle alone — crossing the logo or the CTA
     * shouldn't drop a panel over the page. Closing watches the whole pill, so
     * the cursor can travel from the toggle down into the links without
     * tripping over the gap between them.
     *
     * Whether a pointer hovers at all is decided per interaction rather than
     * per device: `(hover: hover)` describes only the *primary* input, which
     * gets hybrids wrong in both directions — a touch laptop reports hover and
     * would open the menu on enter only to close it again on the click that
     * follows, while an iPad driving a trackpad reports none and would never
     * open at all. pointerType answers what is being used right now, so touch
     * falls through to the tap handler below.
     */
    toggle.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || isOpen) return
        setOpen(true)
        hoverOpenedAt = event.timeStamp
    })

    nav.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return
        // Keyboard focus inside the menu outranks the pointer leaving it. Only
        // *keyboard* focus: a mouse click on the toggle focuses it too, and
        // would otherwise pin the menu open after the cursor has gone.
        if (!nav.querySelector(':focus-visible')) setOpen(false)
    })

    /*
     * Every click toggles — touch, pen, Enter/Space and mouse alike. The one
     * exception is a mouse click that lands just after hover opened the menu:
     * the cursor had to cross the toggle to reach it, so that click is someone
     * asking for the menu that is already opening, not asking to shut it.
     */
    const HOVER_CLICK_GRACE_MS = 500
    let pressedWith = ''
    toggle.addEventListener('pointerdown', (event) => {
        pressedWith = event.pointerType
    })

    toggle.addEventListener('click', (event) => {
        // A keyboard click reports `detail: 0`, which separates it from a press.
        const fromMouse = event.detail !== 0 && pressedWith === 'mouse'
        if (fromMouse && event.timeStamp - hoverOpenedAt < HOVER_CLICK_GRACE_MS) return

        setOpen(!isOpen)
    })

    // Tabbing into the links keeps the menu open. Scoped to the panel, not the
    // pill: the logo and CTA sit in the pill too, and most browsers focus a link
    // on mousedown — so a click on either would open the menu, only for the
    // link-click handler below to shut it again a moment later.
    nav.addEventListener('focusin', (event) => {
        if (panel.contains(event.target)) setOpen(true)
    })

    nav.addEventListener('focusout', (event) => {
        if (!nav.contains(event.relatedTarget)) setOpen(false)
    })

    nav.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !isOpen) return
        setOpen(false)
        toggle.focus()
    })

    // Following a link closes the menu; so does tapping anywhere outside it.
    nav.addEventListener('click', (event) => {
        if (event.target.closest('a')) setOpen(false)
    })

    document.addEventListener('click', (event) => {
        if (!nav.contains(event.target)) setOpen(false)
    })

    // The open height is a fixed pixel value, so re-measure when the panel
    // reflows (wrapping labels, the short-viewport padding in header.css).
    window.addEventListener(
        'resize',
        debounce(() => {
            if (isOpen) panel.style.height = `${content.offsetHeight}px`
        }, 100)
    )
}
